// Global Staff (Super Admin only): head-office users who see every pincode and get their
// features only from the designations assigned here. Backend: /api/global-staff.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';

const API = `${API_BASE_URL}/global-staff`;
const EMPTY_FORM = { firstName: '', lastName: '', email: '', phoneNumber: '', address: '', pinCode: '' };

const errorText = (err, fallback) => (err && err.response && err.response.data && err.response.data.message) || fallback;

/** Module > page (and actions) a set of designations grants. */
const AccessList = ({ access }) => {
  if (!access || access.length === 0) {
    return <Typography color="text.secondary" variant="body2">No access: this user can only log in and open Profile.</Typography>;
  }
  return (
    <Box>
      {access.map((m) => (
        <Box key={m.module} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{m.module}</Typography>
          {m.pages.map((p) => (
            <Box key={p.label} sx={{ ml: 1.5, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.label}</Typography>
              {p.actions.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {p.actions.map((a) => <Chip key={a} label={a} size="small" variant="outlined" />)}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

const GlobalStaffManagement = () => {
  const [rows, setRows] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // create / edit
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited, null = create
  const [form, setForm] = useState(EMPTY_FORM);
  const [formPosts, setFormPosts] = useState([]); // designations chosen while creating
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // assign designations
  const [assignRow, setAssignRow] = useState(null);
  const [assignPosts, setAssignPosts] = useState([]);

  // confirm (block / unblock / reset password) and access preview
  const [confirm, setConfirm] = useState(null); // { type, row }
  const [accessRow, setAccessRow] = useState(null);

  const notify = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const [list, posts] = await Promise.all([
        axios.get(API, { params }),
        axios.get(`${API}/designations`),
      ]);
      setRows(list.data || []);
      setDesignations(posts.data || []);
    } catch (err) {
      notify(errorText(err, 'Error loading global staff'), 'error');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce typing in the search box
    return () => clearTimeout(t);
  }, [load]);

  const designationById = useMemo(() => new Map(designations.map((d) => [d._id, d])), [designations]);

  // ----- create / edit -----
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormPosts([]);
    setErrors({});
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      firstName: row.firstName || '',
      lastName: row.lastName || '',
      email: row.email || '',
      phoneNumber: row.phoneNumber || '',
      address: row.address || '',
      pinCode: row.pinCode && row.pinCode !== '000000' ? row.pinCode : '',
    });
    setErrors({});
    setFormOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!/^\d{10}$/.test(form.phoneNumber.trim())) e.phoneNumber = 'Phone number must be 10 digits';
    if (form.pinCode.trim() && !/^\d{6}$/.test(form.pinCode.trim())) e.pinCode = 'Pincode must be 6 digits';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phoneNumber: form.phoneNumber.trim(),
        address: form.address.trim(),
        pinCode: form.pinCode.trim(),
      };
      if (editing) {
        await axios.put(`${API}/${editing.id}`, body);
        notify('Global staff updated');
      } else {
        const res = await axios.post(API, { ...body, posts: formPosts.map((p) => p._id) });
        notify(res.data.message, res.data.emailSent ? 'success' : 'warning');
      }
      setFormOpen(false);
      load();
    } catch (err) {
      notify(errorText(err, 'Error saving global staff'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ----- assign designations -----
  const openAssign = (row) => {
    setAssignRow(row);
    setAssignPosts(row.posts.map((p) => designationById.get(p._id)).filter(Boolean));
  };

  const handleAssign = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/${assignRow.id}/designations`, { posts: assignPosts.map((p) => p._id) });
      notify('Designations updated');
      setAssignRow(null);
      load();
    } catch (err) {
      notify(errorText(err, 'Error updating designations'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ----- block / unblock / reset password -----
  const handleConfirm = async () => {
    const { type, row } = confirm;
    setSaving(true);
    try {
      if (type === 'reset') {
        const res = await axios.post(`${API}/${row.id}/reset-password`);
        notify(res.data.message);
      } else {
        const res = await axios.patch(`${API}/${row.id}/status`, { status: type === 'block' ? 'inactive' : 'active' });
        notify(res.data.message);
      }
      setConfirm(null);
      load();
    } catch (err) {
      notify(errorText(err, 'Action failed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const assignAccess = useMemo(() => {
    // merge the access of the chosen designations for the preview
    const modules = new Map();
    assignPosts.forEach((d) => (d.access || []).forEach((m) => {
      if (!modules.has(m.module)) modules.set(m.module, new Map());
      const pages = modules.get(m.module);
      m.pages.forEach((p) => {
        const actions = new Set([...(pages.get(p.label) || []), ...p.actions]);
        pages.set(p.label, [...actions]);
      });
    }));
    return [...modules].map(([module, pages]) => ({
      module,
      pages: [...pages].map(([label, actions]) => ({ label, actions })),
    }));
  }, [assignPosts]);

  const columns = [
    { field: 'staffId', headerName: 'State Staff ID', width: 150 },
    {
      field: 'name', headerName: 'Name', width: 190,
      valueGetter: (value, row) => `${row.firstName} ${row.lastName}`,
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.value}</Typography>,
    },
    { field: 'email', headerName: 'Email', width: 230 },
    { field: 'phoneNumber', headerName: 'Mobile', width: 130 },
    {
      field: 'posts', headerName: 'Designations', width: 260, sortable: false,
      renderCell: (p) => (p.row.posts.length === 0
        ? <Chip label="Not assigned" size="small" variant="outlined" />
        : (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {p.row.posts.map((d) => (
              <Chip key={d._id} label={d.postName} size="small" color={d.status === 'active' ? 'primary' : 'default'} />
            ))}
          </Box>
        )),
    },
    {
      field: 'permissionCount', headerName: 'Access', width: 130, sortable: false,
      renderCell: (p) => (
        <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setAccessRow(p.row)}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          {p.row.permissionCount} rights
        </Button>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (p) => (
        <Chip label={p.value === 'active' ? 'Active' : 'Blocked'} size="small" color={p.value === 'active' ? 'success' : 'default'}
          sx={{ fontWeight: 700 }} />
      ),
    },
    {
      field: 'actions', headerName: 'Actions', width: 210, sortable: false,
      renderCell: (p) => (
        <Box>
          <Tooltip title="Edit details"><IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Assign designations"><IconButton size="small" color="primary" onClick={() => openAssign(p.row)}><AssignmentIndIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Reset password"><IconButton size="small" onClick={() => setConfirm({ type: 'reset', row: p.row })}><LockResetIcon fontSize="small" /></IconButton></Tooltip>
          {p.row.status === 'active'
            ? <Tooltip title="Block"><IconButton size="small" color="error" onClick={() => setConfirm({ type: 'block', row: p.row })}><BlockIcon fontSize="small" /></IconButton></Tooltip>
            : <Tooltip title="Unblock"><IconButton size="small" color="success" onClick={() => setConfirm({ type: 'unblock', row: p.row })}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>}
        </Box>
      ),
    },
  ];

  const field = (name, label, extra = {}) => (
    <TextField
      fullWidth size="small" label={label} value={form[name]}
      onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
      error={!!errors[name]} helperText={errors[name]} {...extra}
    />
  );

  const confirmText = confirm && {
    block: `Block ${confirm.row.firstName} ${confirm.row.lastName}? They are signed out of the admin panel immediately and cannot log in until unblocked.`,
    unblock: `Unblock ${confirm.row.firstName} ${confirm.row.lastName}? They can log in again with their current designations.`,
    reset: `Reset the password of ${confirm.row.firstName} ${confirm.row.lastName}? A new password is emailed to ${confirm.row.email}.`,
  }[confirm.type];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Global Staff</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            Head-office users who see data for every pincode. They can open only the pages their designations allow, and
            only you can see, edit, block or assign designations to them.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ fontWeight: 700 }}>
          Create Global Staff
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }} elevation={0} variant="outlined">
        <TextField size="small" label="Search name, email or mobile" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 280 }} />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Blocked</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          rowHeight={64}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          localeText={{ noRowsLabel: 'No global staff yet. Use "Create Global Staff" to add one.' }}
          sx={{
            border: 'none',
            backgroundColor: '#fff',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc', fontWeight: 700 },
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          }}
        />
      </Box>

      {/* create / edit */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit Global Staff' : 'Create Global Staff'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>{field('firstName', 'First name')}</Grid>
            <Grid item xs={12} sm={6}>{field('lastName', 'Last name')}</Grid>
            <Grid item xs={12}>{field('email', 'Email (login username)', { type: 'email' })}</Grid>
            <Grid item xs={12} sm={6}>{field('phoneNumber', 'Mobile number', { inputProps: { maxLength: 10 } })}</Grid>
            <Grid item xs={12} sm={6}>{field('pinCode', 'Pincode (optional)', { inputProps: { maxLength: 6 } })}</Grid>
            <Grid item xs={12}>{field('address', 'Address (optional)')}</Grid>
            {!editing && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple size="small" options={designations} value={formPosts}
                  getOptionLabel={(o) => o.postName} isOptionEqualToValue={(a, b) => a._id === b._id}
                  onChange={(e, v) => setFormPosts(v)}
                  renderInput={(params) => <TextField {...params} label="Designations (optional, can be assigned later)" />}
                />
              </Grid>
            )}
          </Grid>
          {!editing && (
            <Alert severity="info" sx={{ mt: 2 }}>
              A password is generated and emailed to the address above. This user is created at level 3 with access to all pincodes.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (editing ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* assign designations */}
      <Dialog open={!!assignRow} onClose={() => !saving && setAssignRow(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Assign designations{assignRow ? `: ${assignRow.firstName} ${assignRow.lastName}` : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The user gets the combined rights of every designation chosen. Designations and their rights are managed in
            Master &gt; Manage Designation and Designation Privileges.
          </DialogContentText>
          <Autocomplete
            multiple size="small" options={designations} value={assignPosts}
            getOptionLabel={(o) => o.postName} isOptionEqualToValue={(a, b) => a._id === b._id}
            onChange={(e, v) => setAssignPosts(v)}
            renderInput={(params) => <TextField {...params} label="Designations" />}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>What this user will be able to open</Typography>
          <Box sx={{ maxHeight: 340, overflowY: 'auto', p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}>
            <AccessList access={assignAccess} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignRow(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save designations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* access preview */}
      <Dialog open={!!accessRow} onClose={() => setAccessRow(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Access: {accessRow ? `${accessRow.firstName} ${accessRow.lastName}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {accessRow && <AccessList access={accessRow.access} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAccessRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* confirm block / unblock / reset */}
      <Dialog open={!!confirm} onClose={() => !saving && setConfirm(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirm && { block: 'Block user', unblock: 'Unblock user', reset: 'Reset password' }[confirm.type]}
        </DialogTitle>
        <DialogContent><DialogContentText>{confirmText}</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color={confirm && confirm.type === 'block' ? 'error' : 'primary'} onClick={handleConfirm} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GlobalStaffManagement;
